import _ from "lodash"
import { get } from "lodash"
import { getIconPathString } from "../components/icons/pathString"

export const parseLevelText = (feature, lang = "en") => {
  if (!feature?.properties?.level && !feature?.properties?.level_name) return ""

  const { level, level_name } = feature.properties
  const levelName = level?.properties?.name.en || level_name

  return levelName
}

export const getFeatureId = (feature, defaultValue = "") =>
  get(feature, "id", defaultValue)

export const getFeatureName = (feature, defaultValue = "") =>
  get(feature, "properties.name", defaultValue)

export const getFeatureDescription = (feature, defaultValue = "") =>
  get(feature, "properties.description", defaultValue)

export const getCategoryName = (feature) => {
  const { properties, feature_type } = feature
  switch (feature_type) {
    case "amenity": {
      return `${feature_type}.${properties.category}`
    }
    case "occupant": {
      const localParentCategory = get(
        feature,
        "properties.local_parent_categories"
      )
      return localParentCategory.map((category) =>
        get(category, "properties.name", "")
      )
    }
    default:
      return ""
  }
}

const getAmenityDetail = (feature) => {
  const { feature_type } = feature
  const { units, category, venue, description } = feature?.properties || {}
  const categoryName = `${feature_type}.${category}`
  const levelName = get(units, "[0].properties.level.properties.name.en")
  const nameObject = getFeatureName(feature)
  const nameText = _.isEmpty(nameObject) ? categoryName : nameObject
  const zoneName = get(units, "[0].properties.section.properties.name", "")
  let locationDescription = get(feature, "properties.location_description", "")
  locationDescription = !_.isEmpty(locationDescription)
    ? locationDescription
    : null

  const locationName = getLocationNameFromFeature(feature)

  const venueProperties = get(venue, "properties")
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url

  return {
    id: getFeatureId(feature),
    name: nameText,
    level: levelName,
    zone: zoneName,
    category: categoryName,
    location: locationName,
    description,
    locationDescription: locationDescription,
    venueLogoSimple,
    venueLogo,
  }
}

const getUnitDetail = (feature) => {
  const { name, level, section } = feature?.properties || {}
  const levelName = getFeatureName(level)
  const zoneName = getFeatureName(section)
  const locationName = getLocationNameFromFeature(feature)

  return {
    name,
    level: levelName,
    zone: zoneName,
    id: getFeatureId(feature),
    location: locationName,
  }
}
const getEventDetail = (feature) => {
  const {
    name,
    description,
    cover_image: coverImage,
    gallery,
  } = feature?.properties || {}
  const locationName = getLocationNameFromFeature(feature)
  const coverImageUrl = coverImage?.url
  const levelName = parseLevelText(feature.properties.feature)

  // cover_image
  return {
    name,
    description,
    logo: coverImageUrl,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    level: levelName,
  }
}

const getPromotionDetail = (feature) => {
  const {
    description,
    cover_image: coverImage,
    gallery,
    name,
  } = feature?.properties || {}
  const locationName = getLocationNameFromFeature(feature)
  const coverImageUrl = coverImage?.url
  const levelName = parseLevelText(feature.properties.feature)
  // cover_image
  return {
    name,
    description,
    logo: coverImageUrl,
    id: getFeatureId(feature),
    location: locationName,
    gallery,
    level: levelName,
  }
}

const getKioskDetail = (feature) => {
  const { name, level, section } = feature?.properties || {}
  const zoneName = get(section, "properties.name") || ""
  const levelName = getFeatureName(level)
  const locationName = getLocationNameFromFeature(feature)

  return {
    name,
    zone: zoneName,
    level: levelName,
    id: getFeatureId(feature),
    location: locationName,
  }
}

export const getVenueDetail = (venue) => {
  const {
    name,
    logo: logoField,
    logo_simple: logoSimpleField,
  } = venue?.properties || {}

  const logoSimple =
    logoSimpleField?.formats?.thumbnail?.url || logoSimpleField?.url
  const logo = logoField?.formats?.thumbnail?.url || logoField?.url

  return {
    id: getFeatureId(venue),
    name,
    logoSimple,
    logo,
  }
}

const getOccupantDetail = (occupant) => {
  const { properties } = occupant
  const {
    name,
    local_parent_categories,
    logo,
    featured_image,
    cover_image,
    level_name,
    anchor,
    hours,
    phone,
    groups,
    venue,
    is_featured,
  } = occupant?.properties || {}
  const venueProperties = get(venue, "properties")

  const categoryName = local_parent_categories?.[0]?.properties.name
  const groupName = groups?.[0]?.properties.name
  const zoneName = get(anchor, "properties.section.properties.name") || ""
  const logoImage = logo?.formats?.thumbnail?.url || logo?.url
  const featuredImage =
    featured_image?.formats?.thumbnail?.url || featured_image?.url
  const coverImage = cover_image?.formats?.large?.url || cover_image?.url
  const venueLogoSimple =
    venueProperties?.logo_simple?.formats?.thumbnail?.url ||
    venueProperties?.logo_simple?.url
  const venueLogo =
    venueProperties?.logo?.formats?.thumbnail?.url || venueProperties?.logo?.url
  const website = get(properties, "website")
  const gallery = get(properties, "gallery")
  const promotions = get(properties, "promotions")
  const privileges = get(properties, "privileges")
  const description = getFeatureDescription(occupant)
  const locationName = getLocationNameFromFeature(occupant)
  const isFeatured = is_featured

  return {
    id: getFeatureId(occupant),
    zone: zoneName,
    logo: logoImage,
    featuredImage,
    coverImage,
    category: categoryName,
    venueLogoSimple,
    venueLogo,
    phone,
    level: level_name,
    name,
    hours,
    website,
    gallery,
    promotions,
    privileges,
    group: groupName,
    description: description,
    location: locationName,
    isFeatured,
  }
}
const getGeolocationDetail = (geolocation) => {
  const { name, level_name, venue } = geolocation?.properties || {}

  return {
    id: getFeatureId(geolocation),
    name,
    level: level_name,
    venue,
  }
}

const getLevelDetail = (level) => {
  const { properties } = level
  const { name, ordinal } = properties || {}
  return {
    id: getFeatureId(level),
    name,
    ordinal,
  }
}

export const getTaxonomyDetail = (taxonomy) => {
  const { properties } = taxonomy
  const { name } = properties || {}
  return { id: getFeatureId(taxonomy), name }
}

export const getLocationNameFromFeature = (feature) => {
  const { feature_type } = feature || {}
  const properties = get(feature, "properties")

  switch (feature_type) {
    case "unit":
      return getFeatureName(get(properties, "section"))
    case "kiosk":
      return getFeatureName(
        get(properties, "anchor.properties.unit.properties.section")
      )
    case "amenity":
      return getFeatureName(get(properties, "units[0]"))
    case "occupant":
      const kiosk = get(properties, "kiosk")
      const unit = get(properties, "anchor.properties.unit")
      return getFeatureName(kiosk) || getFeatureName(unit)
    case "event":
    case "promotion":
    case "level":
      const feature = get(properties, "feature")
      return getFeatureName(feature)
    default:
      return null
  }
}

export const getFeatureDetail = (feature) => {
  const { feature_type } = feature || {}
  switch (feature_type) {
    case "amenity":
      return getAmenityDetail(feature)
    case "unit":
      return getUnitDetail(feature)
    case "kiosk":
      return getKioskDetail(feature)
    case "occupant":
      return getOccupantDetail(feature)
    case "event":
      return getEventDetail(feature)
    case "promotion":
      return getPromotionDetail(feature)
    case "level":
      return getLevelDetail(feature)
    case "taxonomy":
      return getTaxonomyDetail(feature)
    case "venue":
      return getVenueDetail(feature)
    case "geolocation":
      return getGeolocationDetail(feature)
    default:
      return null
  }
}

/** Create ListItem Props  */
export const listItemPropsParserFactory =
  ({ t, hideLevel }) =>
  (feature) => {
    const { feature_type, id, properties } = feature
    switch (feature_type) {
      case "amenity": {
        const {
          group,
          name,
          /* category,  */ level,
          locationDescription,
          zone,
        } = getFeatureDetail(feature)
        /* const categoryText = t(category, { ns: "imdf" }) */
        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`

        const locationDescriptionText =
          locationDescription && t(locationDescription)
        const primary =
          // The name can be either an IMDF category name ("restroom.female") or a custom name ("Toilet A").
          t(name, { ns: "imdf" })
        const groupText = group && t(group)
        const secondary = locationDescriptionText
          ? _.compact([floorText, locationDescriptionText]).join(" - ") // Floor GF - Next to McDonald
          : _.compact([
              _.compact([floorText, groupText]).join(" - "),
              zoneText,
            ]).join(" | ")
        const avatarSrc =
          properties?.logo?.formats?.thumbnail?.url || properties?.logo?.url
        const categoryIconPath = getIconPathString(properties.category)

        return {
          id,
          avatarProps: {
            src: avatarSrc,
            path: categoryIconPath,
          },
          primary,
          secondary,
          ...feature,
        }
      }
      case "occupant":
      default: {
        const { group, name, category, level, zone } = getFeatureDetail(feature)
        const categoryText = category && t(category, { ns: "imdf" })

        const floorText =
          !hideLevel && `${t("Level Text", { level: t(level) })}`
        const zoneText = zone && `${t("Zone")} ${t(zone)}`

        const primary = t(name)
        const groupText = group && t(group)
        const secondary = _.compact([
          _.compact([categoryText, floorText, groupText]).join(" - "),
          zoneText,
        ]).join(" | ")

        const avatarSrc =
          properties?.logo?.formats?.thumbnail?.url || properties?.logo?.url
        const categoryIconPath =
          properties?.local_parent_categories[0]?.properties.icon_path

        return {
          id,
          avatarProps: {
            src: avatarSrc,
            path: categoryIconPath,
          },
          primary,
          secondary,
          ...feature,
        }
      }
    }
  }
